/**
 * <sticky-atc> — sticky add-to-cart bar for the product page.
 *
 * It never talks to /cart/add itself: it mirrors the state of Dawn's
 * <product-info> / <product-form> and re-submits that same form, so the cart
 * drawer, error handling and analytics keep working exactly as in stock Dawn.
 *
 * Markup: snippets/sticky-atc.liquid. Degrades: with no <product-form> on the page the bar stays hidden unless it
 * is configured in "link" mode.
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.unsubscribeVariantChange = undefined;
        this.observer = undefined;
      }

      connectedCallback() {
        this.bar = this.querySelector('[data-sticky-bar]');
        if (!this.bar) return;

        this.mode = this.dataset.mode === 'link' ? 'link' : 'product';
        this.priceTarget = this.querySelector('[data-sticky-price]');
        this.imageTarget = this.querySelector('[data-sticky-image]');
        this.optionSelects = Array.from(this.querySelectorAll('[data-sticky-option]'));
        this.submitButton = this.querySelector('[data-sticky-submit]');

        if (this.mode === 'product' && !this.resolveProductRefs()) {
          this.hidden = true;
          return;
        }

        this.bindEvents();
        this.watchAnchor();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.unsubscribeVariantChange?.();
      }

      /** Locates Dawn's product form. Returns false when the page has none. */
      resolveProductRefs() {
        this.productInfo = document.querySelector('product-info');
        this.dawnProductForm = this.productInfo?.querySelector('product-form');
        this.dawnForm = this.dawnProductForm?.querySelector('form[data-type="add-to-cart-form"]');
        this.dawnSubmit = this.productInfo?.querySelector('.product-form__submit');
        this.variantSelects = this.productInfo?.querySelector('variant-selects');
        this.quantityInput = this.productInfo?.querySelector('.quantity__input');
        return Boolean(this.dawnForm && this.dawnSubmit);
      }

      bindEvents() {
        if (this.mode !== 'product') return;

        this.submitButton?.addEventListener('click', this.onSubmitClick.bind(this));
        this.optionSelects.forEach((select) => select.addEventListener('change', this.onOptionSelectChange.bind(this)));

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }
      }

      /**
       * Shows the bar only once the main add-to-cart button has scrolled out of
       * view above - under the sticky header or off the top. While it is on
       * screen or still below the fold, the page's own button is the one to
       * use (Mauro: a bar from the first screen was too early, and two red
       * buttons showed at once). IntersectionObserver only - no scroll listener.
       */
      watchAnchor() {
        if (this.dataset.display === 'always') {
          this.toggleBar(true);
          return;
        }

        this.anchor = this.mode === 'product' ? this.dawnSubmit : document.querySelector('[data-sticky-anchor]');
        if (!this.anchor) {
          this.toggleBar(true);
          return;
        }

        this.observe();
        window.addEventListener('resize', () => {
          window.cancelAnimationFrame(this.resizeFrame);
          this.resizeFrame = window.requestAnimationFrame(() => this.observe());
        });
      }

      /** The visible band starts under the sticky header. */
      rootMargin() {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        return `-${Math.round(header)}px 0px 0px 0px`;
      }

      observe() {
        this.observer?.disconnect();
        this.observer = new IntersectionObserver(
          ([entry]) => {
            const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;
            this.toggleBar(scrolledPast);
          },
          { rootMargin: this.rootMargin(), threshold: 0 }
        );
        this.observer.observe(this.anchor);
      }

      /** Re-points the observer after Dawn re-rendered the product form. */
      rearmObserver() {
        if (!this.observer || !this.dawnSubmit) return;
        this.anchor = this.dawnSubmit;
        this.observe();
      }

      toggleBar(visible) {
        this.classList.toggle('sticky-atc--visible', visible);
        // Keyboard focus is scrolled clear of the bar while it covers the
        // bottom of the screen (WCAG 2.4.11).
        const root = document.documentElement;
        if (visible) root.style.setProperty('scroll-padding-bottom', `${this.bar.offsetHeight}px`);
        else root.style.removeProperty('scroll-padding-bottom');
        this.bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
        this.bar.querySelectorAll('a, button, select, input').forEach((node) => {
          node.tabIndex = visible ? 0 : -1;
        });
      }

      onSubmitClick(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        // Dawn can replace the whole <product-info> subtree on variant change.
        if (!this.dawnForm?.isConnected && !this.resolveProductRefs()) return;

        this.syncQuantityToDawnForm();
        this.setLoading(true);
        this.dawnForm.requestSubmit();
        // Dawn owns the request lifecycle; release the local spinner shortly after.
        window.setTimeout(() => this.setLoading(false), 1200);
      }

      syncQuantityToDawnForm() {
        const localQuantity = this.querySelector('[data-sticky-quantity]');
        if (!localQuantity || !this.quantityInput) return;

        const value = parseInt(localQuantity.value, 10);
        if (!Number.isFinite(value) || value < 1) return;

        this.quantityInput.value = value;
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      setLoading(isLoading) {
        this.submitButton?.classList.toggle('loading', isLoading);
        this.submitButton?.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      /**
       * Pushes the bar's selection back into Dawn's <variant-selects> so the
       * normal option-change pipeline (price, media, availability) runs once.
       */
      onOptionSelectChange() {
        if (!this.variantSelects) return;

        const values = this.optionSelects.map((select) => select.value);
        if (!values.length) return;

        const groups = this.variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown');
        let lastTouched = null;

        groups.forEach((group, index) => {
          const value = values[index];
          if (value === undefined) return;
          lastTouched = this.applyOptionValue(group, value) || lastTouched;
        });

        if (lastTouched) lastTouched.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /** Selects `value` inside one option group without firing a change event. */
      applyOptionValue(group, value) {
        const select = group.querySelector('select');
        if (select) {
          const match = Array.from(select.options).find((item) => item.value === value);
          if (!match) return null;
          select.value = value;
          return select;
        }

        const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((item) => item.value === value);
        if (!radio) return null;
        radio.checked = true;
        return radio;
      }

      onVariantChange({ data }) {
        if (!data) return;
        const { html, variant, sectionId } = data;

        if (!this.dawnSubmit?.isConnected) {
          this.resolveProductRefs();
          this.rearmObserver();
        }

        if (!variant) {
          this.setUnavailable(window.variantStrings?.unavailable);
          return;
        }

        this.updatePrice(html, sectionId);
        this.updateImage(variant);
        this.updateSummary(variant.options);
        this.syncOptionSelects(variant.options);

        if (!variant.available) {
          this.setUnavailable(window.variantStrings?.soldOut);
          return;
        }

        this.submitButton?.removeAttribute('aria-disabled');
        this.submitButton?.removeAttribute('disabled');
        if (window.variantStrings?.addToCart) this.setLabel(window.variantStrings.addToCart);
      }

      /** The visible label (desktop) and the icon button's accessible name (phone). */
      setLabel(text) {
        const label = this.submitButton?.querySelector('[data-sticky-submit-label]');
        if (label) label.textContent = text;
        this.submitButton?.setAttribute('aria-label', text);
      }

      updatePrice(html, sectionId) {
        if (!this.priceTarget || !html) return;
        const source = html.querySelector(`#price-${sectionId}`);
        if (source) this.priceTarget.innerHTML = source.innerHTML;
      }

      updateImage(variant) {
        const image = this.imageTarget?.querySelector('img');
        const src = variant.featured_image?.src;
        if (!image || !src) return;
        image.src = `${src}${src.includes('?') ? '&' : '?'}width=200`;
        image.alt = variant.featured_image.alt || image.alt;
      }

      updateSummary(optionValues) {
        const summary = this.querySelector('[data-sticky-summary]');
        if (summary && Array.isArray(optionValues)) summary.textContent = optionValues.join(' · ');
      }

      /** Mirrors the variant Dawn settled on back into the bar's dropdowns. */
      syncOptionSelects(optionValues) {
        if (!Array.isArray(optionValues)) return;

        this.optionSelects.forEach((select, index) => {
          const value = optionValues[index];
          if (value === undefined) return;
          if (Array.from(select.options).some((option) => option.value === value)) select.value = value;
        });
      }

      setUnavailable(message) {
        if (!this.submitButton) return;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.setAttribute('disabled', 'disabled');
        if (message) this.setLabel(message);
      }
    }
  );
}
